import {
  SAVE_ENTITY_DATA,
  CLEAR_ENTITY_DATA,
  QUEUE_SAVE,
  AUTOSAVE_SUCCESS,
} from 'common/types';
import DynamicComponentActions from 'go/actions/dynamicComponent';
import CollaborationActions from 'go/actions/api/collaboration';
import {call, take, select, fork, put} from 'redux-saga/effects';
import _ from 'lodash';
import ConcurrentEditProcessor from 'framework/classes/concurrentEditProcessor';
import {
  ENDPOINT_ORGANIZATION,
  ENDPOINT_DYNAMIC_DATA,
  ENDPOINT_PANEL_V2_SESSION,
  ENDPOINT_WORKFLOW_TASK,
  ENDPOINT_FUNDING_OPPORTUNITY,
} from 'common/endpoints';
import {getInfo, isRoomManagedPath} from 'common/storeUtils';
import goStore from 'go/store';
import autoSave from 'go/actions/api/autoSave';
import {BREAKPOINTS, debugBreakpoint} from 'common/debug';

export const makeServiceCallForEntityType = (token, entityType, dynamicComponent) => {
  switch (entityType) {
    case 'riskAssessment':
      return (data) => riskAssessmentSave(data, dynamicComponent, token, entityType);
    case 'workflowTask':
      return (data) => workflowProcessVariablesSave(data, token, entityType);
    case 'panelReview':
      return (data) => panelReviewSave(data, dynamicComponent, token, entityType);
    case 'fundingOpportunityDraft':
      return (data) =>
        fundingOpportunityDraftSave(data, dynamicComponent, token, entityType);
    case 'fundingOpportunity':
      return (data) => fundingOpportunitySave(data, dynamicComponent, token, entityType);
    default:
      return (data) => defaultSave(data, token, entityType);
  }
};

export const getVersionIdPathByEntityType = (entityType) => {
  switch (entityType) {
    case 'riskAssessment':
    case 'workflowTask':
    case 'refItems':
    case 'eligibility':
    case 'panelReview':
    case 'fundingOpportunity':
    case 'fundingOpportunityDraft':
      return undefined;
    case 'data':
      return 'data.versionId';
    default:
      return _.join(['context', entityType, 'versionId'], '.');
  }
};

export default function* saveEntitySaga() {
  const action = yield take([SAVE_ENTITY_DATA, CLEAR_ENTITY_DATA, AUTOSAVE_SUCCESS]);
  yield fork(saveFromReduxStateToService, action);
  yield call(saveEntitySaga);
}

export function* saveFromReduxStateToService(action) {
  const state = yield select();
  debugBreakpoint(BREAKPOINTS.collaborationLocalSync);
  if (isRoomManagedPath(state, action)) {
    yield put(DynamicComponentActions.validateConfigRules());
    yield put(
      CollaborationActions.localUpdated({
        ...action,
        collaborationSaveType: action.type,
      })
    );
    return;
  }
  const token = _.get(state, 'auth.token');
  const savingData = _.get(state, 'dynamicComponent.savingData');
  const debouncedSave = _.get(state, 'dynamicComponent.debouncedSave');
  const entityType = _.get(action, 'entityType', _.get(action, 'meta.entityType'));
  const entityTypeReduxPath = _.get(action, 'entityTypeReduxPath');
  const patchRequest = _.get(action, 'patchRequest', false);
  if (
    (entityType && !savingData && action.type !== AUTOSAVE_SUCCESS) ||
    (entityType &&
      action.type === AUTOSAVE_SUCCESS &&
      _.includes(debouncedSave, entityType))
  ) {
    let entityData = entityTypeReduxPath
      ? _.get(state, `dynamicComponent.${entityTypeReduxPath}`)
      : entityType !== 'data'
      ? _.get(state, ['dynamicComponent', 'context', entityType])
      : _.get(state, ['dynamicComponent', 'data']);
    const versionIdPath = entityTypeReduxPath
      ? _.join([entityTypeReduxPath, 'versionId'], '.')
      : getVersionIdPathByEntityType(entityType);
    if (versionIdPath === undefined) {
      let serviceCall = makeServiceCallForEntityType(
        token,
        entityType,
        state.dynamicComponent
      );
      yield put(() => {
        const action = serviceCall(entityData);
        return action;
      });
    } else {
      yield call(() =>
        ConcurrentEditProcessor.next({
          entityType: entityType,
          entityId: entityData.entityId,
          from: 'saveEntitySaga',
          entityTypeReduxPath: entityTypeReduxPath,
          patchRequest: patchRequest,
        })
      );
    }
  } else if (
    entityType &&
    savingData === true &&
    !_.includes(debouncedSave, entityType) &&
    action.type !== AUTOSAVE_SUCCESS
  ) {
    yield put(DynamicComponentActions.queueSave(entityType));
  }
  yield put(DynamicComponentActions.validateConfigRules());
}

export const riskAssessmentSave = (data, dynamicComponent, token, entityType) => {
  const programId = _.get(
    dynamicComponent,
    'context.workflowTask.processVariables.programId'
  );

  const payload = {};
  payload.riskLevel = _.get(data, '[0].riskLevel');
  payload.riskComments = _.get(data, '[0].riskComments');
  payload.riskAssessmentId = _.get(data, '[0].riskAssessmentId');
  payload.fundingOpportunityId = _.get(
    dynamicComponent,
    'context.workflowTask.processVariables.fundingOpportunityId'
  );

  payload.organizationId = _.toNumber(
    _.get(dynamicComponent, 'context.awardRecommendation.organizationId')
  );

  payload.grantId = _.get(
    dynamicComponent,
    'context.workflowTask.processVariables.grantId'
  );

  const methodToUse = 'PUT';

  const endpoint = `${ENDPOINT_ORGANIZATION}${`program/${programId}/risk`}`;

  return autoSave.handleOfflineSave(token, endpoint, methodToUse, payload, entityType);
};

export const panelReviewSave = (data, dynamicComponent, token, entityType) => {
  const scores = _.get(dynamicComponent, 'context.panelReviewUserScoresV2.payload');
  const {sessionId, entityId} = _.get(
    dynamicComponent,
    'context.workflowTask.processVariables'
  );
  let payload = [];
  for (let score of scores) {
    payload.push({
      userId: dynamicComponent.context.auth.loginName,
      entityId: entityId,
      part: score.part,
      partId: score.partId,
      factor: score.factor,
      value: score.value,
      text: score.text,
      sessionId: sessionId,
      justification: score.justification,
      version: score.version,
      sortOrder: score.sortOrder,
    });
  }

  const endpoint = `${ENDPOINT_PANEL_V2_SESSION}${sessionId}/entities/${entityId}/scores`;

  return autoSave.handleOfflineSave(token, endpoint, 'POST', payload, entityType);
};

export const defaultSave = (data, token, entityType) => {
  const endpoint = `${ENDPOINT_DYNAMIC_DATA}entity/${_.get(data, 'entityId')}`;
  return autoSave.handleOfflineSave(token, endpoint, 'PUT', data, entityType);
};

export const workflowProcessVariablesSave = (data, token, entityType) => {
  const payload = _.get(data, 'processVariables');
  const taskId = _.get(data, 'task.id');

  const endpoint = `${ENDPOINT_WORKFLOW_TASK}${taskId}`;

  return autoSave.handleOfflineSave(
    token,
    endpoint,
    'POST',
    {variables: payload},
    entityType
  );
};

export const fundingOpportunityDraftSave = (
  data,
  dynamicComponent,
  token,
  entityType
) => {
  const payload = _.get(dynamicComponent, 'context.fundingOpportunityDraft');
  const draftId = _.get(dynamicComponent, 'context.fundingOpportunityDraft.draftId');
  const endpoint = `${ENDPOINT_FUNDING_OPPORTUNITY}draft/${draftId}`;

  return autoSave.handleOfflineSave(token, endpoint, 'PUT', payload, entityType);
};

export const fundingOpportunitySave = (data, dynamicComponent, token, entityType) => {
  const foId = _.get(dynamicComponent, 'data.fundingOpportunityId');
  const payload = _.get(dynamicComponent, 'data.configData');
  const endpoint = `${ENDPOINT_FUNDING_OPPORTUNITY}${foId}/config`;

  return autoSave.handleOfflineSave(
    token,
    endpoint,
    'PUT',
    {configData: JSON.stringify(payload)},
    entityType
  );
};
